import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import api from '../api/axiosConfig';
import { UserDto, UserFormData } from '../types';

interface Props {
    show: boolean;
    user?: UserDto | null;
    onClose: () => void;
    onSaved: () => void;
}

const emptyForm: UserFormData = {
    username: '', email: '', firstname: '', lastname: '', patronomic: '', phone: '', iin: '',
    role: 'DOCTOR', password: '', enabled: true, needPasswordChange: true,
};

const UserModal: React.FC<Props> = ({ show, user, onClose, onSaved }) => {
    const editing = Boolean(user);
    const [form, setForm] = useState<UserFormData>(emptyForm);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!show) return;
        setError(null);
        if (!user) {
            setForm(emptyForm);
            return;
        }
        setForm({
            username: user.username ?? '', email: user.email ?? '', firstname: user.firstname ?? '',
            lastname: user.lastname ?? '', patronomic: user.patronomic ?? user.patronymic ?? '',
            phone: user.phone ?? '', iin: user.iin ?? '', role: user.role?.replace(/^ROLE_/, '') ?? 'DOCTOR',
            password: '', enabled: user.enabled ?? true, needPasswordChange: user.needPasswordChange ?? false,
        });
    }, [show, user]);

    const set = (field: keyof UserFormData, value: string | boolean) =>
        setForm((current) => ({ ...current, [field]: value }));

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);
        setError(null);
        try {
            if (user) {
                await api.put(`/users/${user.id}`, {
                    username: form.username, email: form.email, phone: form.phone || null,
                    iin: form.iin || null, role: form.role, enabled: form.enabled,
                    needPasswordChange: form.needPasswordChange,
                });
            } else {
                await api.post('/users', { ...form, patronomic: form.patronomic });
            }
            onSaved();
        } catch (e: any) {
            const body = e.response?.data;
            setError(typeof body === 'string' ? body : body?.message || 'Не удалось сохранить пользователя');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <Form onSubmit={submit}>
                <Modal.Header closeButton><Modal.Title>{editing ? 'Редактировать пользователя' : 'Новый пользователь'}</Modal.Title></Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Row className="g-3">
                        {!editing && <>
                            <Col md={4}><Form.Label>Фамилия</Form.Label><Form.Control required value={form.lastname} onChange={(e) => set('lastname', e.target.value)} /></Col>
                            <Col md={4}><Form.Label>Имя</Form.Label><Form.Control required value={form.firstname} onChange={(e) => set('firstname', e.target.value)} /></Col>
                            <Col md={4}><Form.Label>Отчество</Form.Label><Form.Control required value={form.patronomic} onChange={(e) => set('patronomic', e.target.value)} /></Col>
                        </>}
                        <Col md={6}><Form.Label>Логин</Form.Label><Form.Control required value={form.username} onChange={(e) => set('username', e.target.value)} /></Col>
                        <Col md={6}><Form.Label>Email</Form.Label><Form.Control required type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Col>
                        <Col md={6}><Form.Label>Телефон</Form.Label><Form.Control value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Col>
                        <Col md={6}><Form.Label>ИИН</Form.Label><Form.Control value={form.iin} onChange={(e) => set('iin', e.target.value)} /></Col>
                        <Col md={6}>
                            <Form.Label>Роль</Form.Label>
                            <Form.Select value={form.role} onChange={(e) => set('role', e.target.value)}>
                                <option value="DOCTOR">Врач</option>
                                <option value="CLINIC_HEAD">Главный врач</option>
                                <option value="ADMIN">Администратор</option>
                            </Form.Select>
                        </Col>
                        {!editing && <Col md={6}><Form.Label>Временный пароль</Form.Label><Form.Control required minLength={6} type="password" value={form.password} onChange={(e) => set('password', e.target.value)} /></Col>}
                        <Col md={6} className="d-flex align-items-end"><Form.Check type="switch" label="Временный пароль: попросить сменить при входе" checked={form.needPasswordChange} onChange={(e) => set('needPasswordChange', e.target.checked)} /></Col>
                        {editing && <Col md={6} className="d-flex align-items-end"><Form.Check type="switch" label="Учётная запись активна" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} /></Col>}
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onClose}>Отмена</Button>
                    <Button type="submit" disabled={loading}>{loading && <Spinner size="sm" className="me-2" />}{editing ? 'Сохранить' : 'Создать'}</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default UserModal;
